/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { EmailRegex } from '../../utilities/regex/email-regex';
import { INosCheckoutGuestProfileData } from './nos-checkout-guest-profile.data';
import { INosCheckoutGuestProfileProps } from './nos-checkout-guest-profile.props.autogenerated';
import getAddContactInfo, { IAddContactInfo } from './components/get-add-contact-info';
import getShowContactInfo, { IShowContactInfo } from './components/get-show-contact-info';

export * from './components/get-add-contact-info';
export * from './components/get-show-contact-info';

interface ICheckoutGuestProfileState {
    email: string;
    purchaseOrderId: string;
}

/**
 * This setting defines PURCHASEORDERID Key.
 */
export const purchaseOrderIdKey = 'PURCHASEORDERID';

export interface ICheckoutGuestProfileModuleProps extends INosCheckoutGuestProfileProps<INosCheckoutGuestProfileData>, IModuleStateProps {}

export interface ICheckoutGuestProfileViewProps
    extends INosCheckoutGuestProfileProps<INosCheckoutGuestProfileData>,
        ICheckoutGuestProfileState {
    moduleProps: IModuleProps;
    showContactInfo?: IShowContactInfo;
    addContactInfo?: IAddContactInfo;

    addPurchaseOrderIdElement?: JSX.Element;
    showPurchaseOrderIdElement?: JSX.Element;

    onChange?(email: string): void;
    onEdit?(): void;
    onSubmit?(): void;
    onCancel?(): void;
}

/**
 *
 * CheckoutGuestProfile component.
 * @extends {React.PureComponent<INosCheckoutGuestProfileProps<INosCheckoutGuestProfileData>, ICheckoutGuestProfileState>}
 */
// @ts-expect-error
@withModuleState
@observer
class CheckoutGuestProfile extends React.Component<ICheckoutGuestProfileModuleProps, ICheckoutGuestProfileState> {
    private purchaseOrderIdRef: React.RefObject<HTMLInputElement>;

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef();

    public constructor(props: ICheckoutGuestProfileModuleProps) {
        super(props);

        this.state = {
            email:
                (this.props.context &&
                    this.props.context.request &&
                    this.props.context.request.user &&
                    this.props.context.request.user.isAuthenticated &&
                    this.props.context.request.user.emailAddress) ||
                '',
            purchaseOrderId: this._getAttributeValue(purchaseOrderIdKey) || ''
        };

        this.purchaseOrderIdRef = React.createRef();
    }

    @computed get isDataReady(): boolean {
        return (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS';
    }

    public componentDidMount(): void {
        when(
            () => this.isDataReady,
            async () => {
                await this.init();
            }
        );

        reaction(
            () => this.props.data.checkout.result?.isExpressCheckoutApplied,
            async isExpressCheckoutApplied => {
                if (isExpressCheckoutApplied && !this.props.data.checkout.result?.isExpressAddressApplied) {
                    const email = this.props.data.checkout.result?.emailAddressFromExpressCheckout ?? '';

                    await this.submitEmail(email);
                }
            }
        );
    }

    public shouldComponentUpdate(nextProps: ICheckoutGuestProfileModuleProps, nextState: ICheckoutGuestProfileState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const {
            config: { className },
            resources,
            moduleState: { isReady, hasError, hasExternalSubmitGroup }
        } = this.props;
        const email = this.props.data.checkout.result?.guestCheckoutEmail ?? '';

        const viewProps: ICheckoutGuestProfileViewProps = {
            ...this.props,
            ...this.state,
            onChange: this.onChange,
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,

            moduleProps: {
                moduleProps: this.props,
                className: classnames('ms-checkout-guest-profile', className, isReady ? 'show' : 'add')
            },
            showContactInfo: isReady
                ? getShowContactInfo({
                      canEdit: !hasExternalSubmitGroup,
                      email,
                      resources,
                      onEdit: this.onEdit
                  })
                : undefined,
            addContactInfo: !isReady
                ? getAddContactInfo({
                      email: this.state.email,
                      hasError,
                      resources,
                      inputRef: this.inputRef,
                      onChange: this.onChange,
                      canSubmit: !hasExternalSubmitGroup,
                      onSubmit: this.onSubmit,
                      canCancel: !hasExternalSubmitGroup && !!email,
                      onCancel: this.onCancel
                  })
                : undefined,
            addPurchaseOrderIdElement: !isReady ? this._renderAddPurchaseOrderId() : undefined,
            showPurchaseOrderIdElement: isReady ? this._renderShowPurchaseOrderId() : undefined
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly init = async (): Promise<void> => {
        const checkoutState = this.props.data.checkout.result;

        const hasInvoiceLine = checkoutState?.checkoutCart.hasInvoiceLine;

        const emailOnCart = checkoutState?.checkoutCart.cart.ReceiptEmail;
        const email = hasInvoiceLine
            ? this.props.context.request.user.emailAddress ?? ''
            : emailOnCart ??
              (checkoutState?.isExpressCheckoutApplied ? checkoutState.emailAddressFromExpressCheckout : undefined) ??
              this.state.email;

        if (email) {
            await this.updateGuestProfile(email);
            checkoutState?.updateHasGuestCheckoutEmail({ newHasGuestCheckoutEmail: true });
        } else {
            checkoutState?.updateHasGuestCheckoutEmail({ newHasGuestCheckoutEmail: false });
        }

        const isEmailValid = this.isEmailValid(email);
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            status: hasInvoiceLine ? 'disabled' : email && isEmailValid ? 'ready' : 'updating',
            hasError: !!email && !isEmailValid
        });
    };

    private readonly onChange = (rowEmail: string): void => {
        const email = (rowEmail || '').replace(new RegExp('[<>]', 'gi'), '');
        this.setState({ email });
        if (this.props.moduleState.hasError) {
            // Clear error during user updating the email
            this.props.moduleState.setHasError(false);
        }
    };

    /**
     * Method _getExtensionPropertyValue.
     * @param fieldName -The checkout address properties.
     */
    private _getAttributeValue = (fieldName: string) => {
        const purchaseOrderId =
            this.props.data.checkout.result &&
            this.props.data.checkout.result.checkoutCart &&
            this.props.data.checkout.result.checkoutCart.cart &&
            this.props.data.checkout.result.checkoutCart.cart?.AttributeValues?.find(attribute => attribute.Name === fieldName);

        if (purchaseOrderId) {
            // @ts-expect-error -- Need to provide data type.
            return purchaseOrderId?.TextValue;
        }
        return undefined;
    };

    /**
     * Method _getExtensionPropertyValue.
     * @param event -The checkout address properties.
     */
    private _onEditPurchaseOrderId = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const textArea: HTMLInputElement | null =
            this.purchaseOrderIdRef && this.purchaseOrderIdRef.current && this.purchaseOrderIdRef.current;
        const purchaseOrderId = (this.purchaseOrderIdRef && this.purchaseOrderIdRef.current && this.purchaseOrderIdRef.current.value) || '';
        const text: string = purchaseOrderId;
        textArea!.value = text;
        this.setState({ purchaseOrderId: text });
    };

    /**
     * Method to render Purchase Order ID.
     */
    private _renderAddPurchaseOrderId = (): JSX.Element => {
        const { purchaseOrderId } = this.state;

        return (
            <div style={{ marginTop: '20px' }}>
                <label id='ms-checkout-guest-profile__label' className='ms-checkout-guest-profile__input-label'>
                    {this.props.resources.purchaseOrderIdLabelText}
                </label>
                <input
                    ref={this.purchaseOrderIdRef}
                    aria-label='Delivery Notes'
                    placeholder={this.props.resources.purchaseOrderIdPlaceHolderText}
                    className='ms-checkout-guest-profile__input-text form-control'
                    value={purchaseOrderId}
                    onChange={this._onEditPurchaseOrderId}
                    maxLength={150}
                />
            </div>
        );
    };

    private readonly _updateAttributeValues = async (attributekey: string, value: string) => {
        await this.props.data.checkout.result?.checkoutCart.updateAttributeValues({
            newAttributeValues: [
                {
                    // @ts-expect-error -- Need to provide data type.
                    '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                    Name: attributekey,
                    TextValue: value,
                    ExtensionProperties: [],
                    TextValueTranslations: []
                }
            ]
        });
    };

    private _renderShowPurchaseOrderId = (): JSX.Element => {
        const { purchaseOrderId } = this.state;

        return (
            <div style={{ marginTop: '10px' }}>
                <label id='ms-checkout-guest-profile__label' className='ms-checkout-guest-profile__input-label'>
                    {this.props.resources.purchaseOrderIdLabelText}
                </label>
                <div className='ms-checkout-guest-profile__purchase-order-id-width'>
                    {purchaseOrderId ? purchaseOrderId : this.props.resources.noPurchaseOrderIdText}
                </div>
            </div>
        );
    };

    private readonly isEmailValid = (email: string): boolean => {
        return EmailRegex.defaultRegex.test(email);
    };

    private readonly updateGuestProfile = async (newGuestCheckoutEmail: string): Promise<void> => {
        if (this.props.data.checkout.result) {
            await this.props.data.checkout.result.updateGuestCheckoutEmail({ newGuestCheckoutEmail });
            this.setState({ email: newGuestCheckoutEmail });
        }
    };

    private readonly onEdit = (): void => {
        this.props.moduleState.onUpdating();
    };

    private readonly onSubmit = async (): Promise<void> => {
        const { email, purchaseOrderId } = this.state;

        this._updateAttributeValues(purchaseOrderIdKey, purchaseOrderId).catch(error => {
            this.props.telemetry.error(`error in _updateExtensionProps ${error}`);
        });

        await this.submitEmail(email);
    };

    /**
     * Submit the email address.
     * @param email - The email to be submitted.
     *
     */
    private readonly submitEmail = async (email: string): Promise<void> => {
        const isValid = this.isEmailValid(email);
        if (!isValid) {
            this.props.moduleState.setHasError(true);

            const input = this.inputRef.current?.focus && (this.inputRef.current as HTMLElement);
            input?.focus();
        } else {
            this.props.moduleState.onReady();
            await this.updateGuestProfile(email);
        }
    };

    private readonly onCancel = (): void => {
        // Reset to the saved value
        if (!this.props.data.checkout.result) {
            return;
        }

        const email = this.props.data.checkout.result.guestCheckoutEmail;
        if (!email) {
            // It has no saved email, set to status updating and request user to fill the email form
            this.props.moduleState.onUpdating();
        } else {
            // It has saved email, set to status ready
            this.setState({
                email
            });
            this.props.moduleState.setHasError(false);
            this.props.moduleState.onReady();
        }
        // Reset purchaseOrderId
        const purchaseOrderId = this._getAttributeValue(purchaseOrderIdKey);
        this.setState({ purchaseOrderId });
    };
}

export default CheckoutGuestProfile;
